import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { CheckCircle2, Clock3, AlertTriangle, MessageCircleMore } from "lucide-react";

function EventDot({ status }) {
  if (status === "done" || status === "sent") return <span className="h-2.5 w-2.5 rounded-full bg-cp-primary-light" />;
  if (status === "failed") return <span className="h-2.5 w-2.5 rounded-full bg-red-400" />;
  if (status === "skipped") return <span className="h-2.5 w-2.5 rounded-full bg-slate-500" />;
  return <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />;
}

export default function LogsPage() {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/recovery/timeline", { params: { limit: 40 } });
      setTimeline(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-6" data-testid="logs-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Historico de recuperacao</h1>
        <p className="text-slate-400 text-sm mt-1">
          Veja a linha do tempo de cada transacao: PIX gerado, mensagens enviadas e pagamento confirmado.
        </p>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-40 border border-white/[0.06] bg-cp-bg rounded-lg animate-pulse" />)}
        </div>
      ) : timeline.length === 0 ? (
        <div className="border border-white/[0.06] bg-cp-bg rounded-lg p-10 text-center">
          <Clock3 className="h-10 w-10 mx-auto text-slate-700 mb-3" />
          <div className="font-heading text-lg">Sem eventos ainda</div>
          <div className="text-sm text-slate-500 mt-1">Assim que um PIX entrar, a linha do tempo aparece aqui.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {timeline.map((tx) => (
            <div key={tx.transaction_id} className="border border-white/[0.06] bg-cp-bg rounded-lg p-4">
              <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
                <div>
                  <div className="font-heading text-lg font-semibold">{tx.customer_name || "Cliente"}</div>
                  <div className="text-xs text-slate-500 font-mono">{tx.transaction_id}</div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-lg text-cp-primary-light">{formatCurrency(tx.amount)}</div>
                  <div className="text-xs text-slate-500 uppercase">{tx.status}</div>
                </div>
              </div>

              <div className="space-y-3 pl-1">
                {(tx.events || []).map((ev) => (
                  <div key={`${tx.transaction_id}-${ev.key}-${ev.at || "na"}`} className="flex gap-3">
                    <div className="pt-1"><EventDot status={ev.status} /></div>
                    <div className="flex-1 min-w-0 border border-white/[0.06] rounded-md px-3 py-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-sm font-medium inline-flex items-center gap-2">
                          {ev.key.startsWith("message_") ? <MessageCircleMore className="h-4 w-4 text-slate-400" /> : <CheckCircle2 className="h-4 w-4 text-slate-400" />}
                          {ev.label}
                        </div>
                        <div className="text-[11px] text-slate-500">{formatDateTime(ev.at)}</div>
                      </div>
                      {ev.message ? (
                        <div className="text-xs text-slate-300 mt-2 whitespace-pre-wrap">{ev.message}</div>
                      ) : null}
                      {ev.status === "failed" ? (
                        <div className="text-xs text-red-300 mt-2 inline-flex items-center gap-1">
                          <AlertTriangle className="h-3.5 w-3.5" /> Falha no envio desta etapa.
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
